import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { WIDGET_DEFINITIONS, type WidgetConfig } from '@shared/widgets';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { fetchUserPreferences, setCachedUserPreferences } from '@/lib/userPreferences';
import { defaultWidgetLayout, mergeNewWidgets, type WidgetPreference } from '@/lib/widgetLayout';

// Widget layout version for migrations
const WIDGET_LAYOUT_VERSION = 8; // v8: added task-classifications and lead-classifications widgets

/**
 * The default dashboard.
 *
 * WHY SEVEN AND NOT EIGHTEEN
 * Every one of the eighteen widgets used to ship `enabled: true`, so a new
 * user's first dashboard was all of them. That is not a default, it is the
 * absence of one — the decision about what matters was handed to someone who
 * has not used the product yet, at the moment they know least about it.
 *
 * A salesperson opening this app is answering three questions, in this order:
 * what is overdue, what am I about to lose, and what have I got moving. So the
 * default is the widgets that answer those, and nothing else:
 *
 *   overdue-followups           what needs doing now
 *   recent-tasks                what is next
 *   at-risk-leads               what goes cold if it is ignored
 *   personal-kpi-panel          how I am doing
 *   buyer-journey-distribution  where the pipeline actually sits
 *   new-leads-counter           what came in
 *   recent-activity-feed        what changed since I last looked
 *
 * The other eleven are reports. They are worth having and they are not worth
 * being shown on arrival: `registrations-trend` and `lead-source-breakdown` are
 * things you go and look at, `team-kpi-panel` is a manager's view rather than a
 * salesperson's, `new-registrations` largely restates `new-leads-counter`, and
 * the three email widgets are only meaningful once a tenant has connected
 * ActiveCampaign — which most have not, so they arrived as three empty states.
 *
 * NOTHING IS REMOVED. All eighteen remain in Configure Widgets, positioned, one
 * switch away. `enabled: false` is a starting point, not a restriction.
 *
 * WHO THIS CHANGES IT FOR
 * New users, and anyone who presses Reset to Defaults. WIDGET_LAYOUT_VERSION is
 * deliberately NOT bumped: existing preferences are already stored per user, so
 * an existing dashboard keeps every widget it has. Bumping the version to "roll
 * out" a taste change would silently delete other people's layouts, which is
 * not a thing a default gets to do.
 *
 * The positions are unchanged so that switching one back on puts it where it
 * has always been rather than at the bottom.
 */
const DEFAULT_WIDGET_LAYOUT: Record<string, WidgetPreference> = defaultWidgetLayout();

/* WHERE THE DEFAULTS LIVE NOW (2026-09-30). Which widgets start on, and in
   what order, is `defaultEnabled` and the definition order in
   shared/widgets.ts — one list the picker, a new user's dashboard and Reset to
   Defaults all read, sorted by area (Overview, Sales, Offers, Availability,
   Leads, Tasks, Team, Email Marketing, Activity) and summary-to-detail inside
   each. The reasoning above still decides what is on; it is simply written
   down once. A widget a SAVED layout has never heard of is placed by
   lib/widgetLayout `mergeNewWidgets`: straight after Needs Attention, nothing
   else moved. */

interface WidgetManagerContextType {
  widgets: Record<string, WidgetPreference>;
  loading: boolean;
  showControls: boolean;
  setShowControls: (show: boolean) => void;
  toggleWidget: (widgetId: string, enabled: boolean) => void;
  configureWidget: (widgetId: string, configuration: Record<string, any>) => void;
  moveWidget: (widgetId: string, position: WidgetPreference['position']) => void;
  reorderWidgets: (newOrder: string[]) => void;
  resetToDefaults: () => void;
  getEnabledWidgets: () => WidgetPreference[];
  getWidgetsByCategory: (category: string) => WidgetPreference[];
  getWidgetConfig: (widgetId: string) => WidgetConfig | null;
  loadWidgetPreferences: () => Promise<void>;
}

const WidgetManagerContext = createContext<WidgetManagerContextType | undefined>(undefined);

interface WidgetManagerProviderProps {
  children: ReactNode;
}

export function WidgetManagerProvider({ children }: WidgetManagerProviderProps) {
  const { user } = useAuth();
  const [widgets, setWidgets] = useState<Record<string, WidgetPreference>>({});
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(false);
  // Debounce ref: batches rapid widget changes (drag-and-drop) into one server write
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const loadWidgetPreferences = async () => {
    try {
      setLoading(true);

      // Fetch server preferences if authenticated (cross-device source of truth)
      // Through the shared cache, so this does not become a third copy of the
      // same request on every dashboard load. See lib/userPreferences.
      const serverPrefs: Record<string, string> = user?.id ? await fetchUserPreferences() : {};

      // Server takes priority; localStorage is the fallback for offline / unauthenticated
      const savedPreferences = serverPrefs.widgetPreferences ?? localStorage.getItem('widget-preferences');
      const savedVersion = parseInt(serverPrefs.widgetLayoutVersion ?? localStorage.getItem('widget-layout-version') ?? '0');
      let widgetPrefs: Record<string, WidgetPreference> = {};
      
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        widgetPrefs = { ...parsed };
        
        // MIGRATION: Apply layout changes for version upgrades
        if (savedVersion < WIDGET_LAYOUT_VERSION) {
          console.log(`Migrating widget layout from version ${savedVersion} to ${WIDGET_LAYOUT_VERSION}`);
          let migrationApplied = false;

          // Version 8 migration: Add task-classifications and lead-classifications
          if (savedVersion < 8 && savedVersion >= 7) {
            console.log('Version 8 migration: Adding task-classifications and lead-classifications widgets');
            const newWidgets = ['task-classifications', 'lead-classifications'];
            newWidgets.forEach(id => {
              if (!widgetPrefs[id] && DEFAULT_WIDGET_LAYOUT[id]) {
                widgetPrefs[id] = { ...DEFAULT_WIDGET_LAYOUT[id] };
              }
            });
            migrationApplied = true;
          }

          // Version 7 migration: Full reset to sales-priority order
          if (savedVersion < 7) {
            console.log('Version 7 migration: Resetting to sales-priority widget order');
            widgetPrefs = { ...DEFAULT_WIDGET_LAYOUT };
            migrationApplied = true;
          }

          // Version 6 migration: Enable overdue-followups widget (kept for historical reference)
          else if (savedVersion < 6) {
            console.log('Version 6 migration: Enable overdue-followups widget');
            if (widgetPrefs['overdue-followups']) {
              widgetPrefs['overdue-followups'].enabled = true;
              widgetPrefs['overdue-followups'].position = { row: 2, col: 9, width: 4, height: 3 };
            }
            migrationApplied = true;
          }

          // Version 5 migration: Add Email Marketing widgets
          if (savedVersion < 5 && savedVersion >= 3) {
            console.log('Version 5 migration: Adding Email Marketing widgets');
            // Add email marketing widgets if they don't exist
            const emailWidgets = ['email-performance', 'recent-campaigns', 'automation-status'];
            emailWidgets.forEach(widgetId => {
              if (!widgetPrefs[widgetId] && DEFAULT_WIDGET_LAYOUT[widgetId]) {
                widgetPrefs[widgetId] = { ...DEFAULT_WIDGET_LAYOUT[widgetId] };
                console.log(`Added email marketing widget: ${widgetId}`);
              }
            });
            migrationApplied = true;
          }
          // Version 3 migration: Force complete refresh to include new widgets (New Registrations, Registrations Trend)
          else if (savedVersion < 3) {
            console.log('Version 3 migration: Resetting to default layout to include new widgets');
            // Force complete reset to ensure new widgets appear
            widgetPrefs = { ...DEFAULT_WIDGET_LAYOUT };
            migrationApplied = true;
          }
          // Version 2 migration: Remap widgets from old to new default positions
          else if (savedVersion < 2) {
            // Old default positions (before Team KPI move)
            const OLD_POSITIONS: Record<string, {row: number, col: number, width: number, height: number}> = {
              'personal-kpi-panel': { row: 1, col: 1, width: 12, height: 3 },
              'recent-tasks': { row: 2, col: 1, width: 6, height: 3 },
              'recent-notes': { row: 2, col: 7, width: 6, height: 3 },
              'recent-files': { row: 3, col: 1, width: 6, height: 3 },
              'at-risk-leads': { row: 3, col: 7, width: 6, height: 3 },
              'buyer-journey-distribution': { row: 4, col: 1, width: 6, height: 3 },
              'recent-activity-feed': { row: 4, col: 7, width: 6, height: 3 },
              'new-leads-counter': { row: 5, col: 1, width: 6, height: 2 },
              'lead-source-breakdown': { row: 5, col: 7, width: 6, height: 3 },
              'team-kpi-panel': { row: 6, col: 1, width: 12, height: 3 },
            };
            
            // Check each widget to see if it matches old default position
            Object.keys(widgetPrefs).forEach(widgetId => {
              const widget = widgetPrefs[widgetId];
              const oldPos = OLD_POSITIONS[widgetId];
              const newPos = DEFAULT_WIDGET_LAYOUT[widgetId]?.position;
              
              // Only migrate if widget is still at old default position AND new position exists
              if (widget.position && oldPos && newPos) {
                const isAtOldPosition = 
                  widget.position.row === oldPos.row &&
                  widget.position.col === oldPos.col &&
                  widget.position.width === oldPos.width &&
                  widget.position.height === oldPos.height;
                
                if (isAtOldPosition) {
                  widget.position = { ...newPos };
                  migrationApplied = true;
                  console.log(`Migrated ${widgetId} from row ${oldPos.row} to row ${newPos.row}`);
                }
              }
            });
          }
          
          // CRITICAL: Save migrated preferences AND version to localStorage
          if (migrationApplied) {
            localStorage.setItem('widget-preferences', JSON.stringify(widgetPrefs));
            console.log(`Widget layout migration v${WIDGET_LAYOUT_VERSION} completed and saved`);
          }
          localStorage.setItem('widget-layout-version', WIDGET_LAYOUT_VERSION.toString());
        }
      } else {
        // First-run: save version for future migrations
        localStorage.setItem('widget-layout-version', WIDGET_LAYOUT_VERSION.toString());
      }
      
      // Ensure all widgets from WIDGET_DEFINITIONS have entries. A first run
      // takes the default layout whole; a saved layout keeps every position it
      // has and gains only the widgets added since it was saved, placed after
      // Needs Attention (lib/widgetLayout).
      if (savedPreferences) {
        widgetPrefs = mergeNewWidgets(widgetPrefs);
      } else {
        Object.keys(WIDGET_DEFINITIONS).forEach(widgetId => {
          if (!widgetPrefs[widgetId]) widgetPrefs[widgetId] = { ...DEFAULT_WIDGET_LAYOUT[widgetId] };
        });
      }
      
      setWidgets(widgetPrefs);
      
      // Save the merged preferences back to localStorage
      if (!savedPreferences || Object.keys(widgetPrefs).length !== Object.keys(JSON.parse(savedPreferences || '{}')).length) {
        localStorage.setItem('widget-preferences', JSON.stringify(widgetPrefs));
      }
    } catch (error) {
      console.error('Failed to load widget preferences:', error);
      setWidgets(DEFAULT_WIDGET_LAYOUT);
    } finally {
      setLoading(false);
    }
  };

  const saveWidgetPreferences = async (updatedWidgets: Record<string, WidgetPreference>) => {
    try {
      // Always write to localStorage immediately for instant same-device reactivity
      localStorage.setItem('widget-preferences', JSON.stringify(updatedWidgets));
      localStorage.setItem('widget-layout-version', WIDGET_LAYOUT_VERSION.toString());

      // Debounced server write (1 s) — batches rapid drag-and-drop moves into one request
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        apiRequest('PATCH', '/api/users/me/preferences', {
          widgetPreferences: JSON.stringify(updatedWidgets),
          widgetLayoutVersion: WIDGET_LAYOUT_VERSION.toString(),
        }).catch(() => {});
      }, 1000);
    } catch (error) {
      console.error('Failed to save widget preferences:', error);
    }
  };

  const toggleWidget = (widgetId: string, enabled: boolean) => {
    // Get existing widget or create default from layout
    const existingWidget = widgets[widgetId];
    const defaultWidget = DEFAULT_WIDGET_LAYOUT[widgetId];
    const definition = WIDGET_DEFINITIONS[widgetId];
    
    // If widget doesn't exist, initialize with defaults
    let widgetData: WidgetPreference;
    if (existingWidget) {
      widgetData = { ...existingWidget, enabled };
    } else if (defaultWidget) {
      widgetData = { ...defaultWidget, enabled };
    } else {
      // Fallback for widgets not in default layout
      widgetData = {
        id: widgetId,
        enabled,
        position: { row: 99, col: 1, width: 6, height: 3 }, // Put new widgets at bottom
        configuration: {}
      };
    }
    
    const updatedWidgets = {
      ...widgets,
      [widgetId]: widgetData
    };
    setWidgets(updatedWidgets);
    saveWidgetPreferences(updatedWidgets);
  };

  const configureWidget = (widgetId: string, configuration: Record<string, any>) => {
    const updatedWidgets = {
      ...widgets,
      [widgetId]: {
        ...widgets[widgetId],
        configuration
      }
    };
    setWidgets(updatedWidgets);
    saveWidgetPreferences(updatedWidgets);
  };

  const moveWidget = (widgetId: string, position: WidgetPreference['position']) => {
    const updatedWidgets = {
      ...widgets,
      [widgetId]: {
        ...widgets[widgetId],
        position
      }
    };
    setWidgets(updatedWidgets);
    saveWidgetPreferences(updatedWidgets);
  };

  const reorderWidgets = (newOrder: string[]) => {
    const updatedWidgets = { ...widgets };
    
    // Update positions based on new order - arrange in grid
    newOrder.forEach((widgetId, index) => {
      if (updatedWidgets[widgetId]) {
        const row = Math.floor(index / 3) + 1; // 3 columns per row
        const col = (index % 3) + 1;
        
        updatedWidgets[widgetId] = {
          ...updatedWidgets[widgetId],
          position: {
            row,
            col: col * 4 - 3, // Space out columns (1, 5, 9)
            width: updatedWidgets[widgetId].position?.width || 6,
            height: updatedWidgets[widgetId].position?.height || 3
          }
        };
      }
    });
    
    setWidgets(updatedWidgets);
    saveWidgetPreferences(updatedWidgets);
  };

  const resetToDefaults = () => {
    // Deep copy to ensure all widget positions and enabled states are reset to original defaults
    const freshDefaults: Record<string, WidgetPreference> = {};
    Object.keys(DEFAULT_WIDGET_LAYOUT).forEach(widgetId => {
      const defaultWidget = DEFAULT_WIDGET_LAYOUT[widgetId];
      freshDefaults[widgetId] = {
        id: defaultWidget.id,
        enabled: defaultWidget.enabled,
        position: defaultWidget.position ? { ...defaultWidget.position } : undefined,
        configuration: defaultWidget.configuration ? { ...defaultWidget.configuration } : undefined
      };
    });
    
    setWidgets(freshDefaults);
    saveWidgetPreferences(freshDefaults);
  };

  // Get enabled widgets sorted by position
  const getEnabledWidgets = () => {
    return Object.values(widgets)
      .filter(widget => widget.enabled)
      .sort((a, b) => {
        if (!a.position || !b.position) return 0;
        if (a.position.row !== b.position.row) {
          return a.position.row - b.position.row;
        }
        return a.position.col - b.position.col;
      });
  };

  // Get widgets by category
  const getWidgetsByCategory = (category: string) => {
    return Object.values(widgets).filter(widget => {
      const definition = WIDGET_DEFINITIONS[widget.id];
      return definition?.category === category;
    });
  };

  // Get widget configuration
  const getWidgetConfig = (widgetId: string): WidgetConfig | null => {
    const preference = widgets[widgetId];
    const definition = WIDGET_DEFINITIONS[widgetId];
    
    if (!preference || !definition) return null;
    
    return {
      ...definition,
      enabled: preference.enabled,
      position: preference.position,
      configuration: preference.configuration
    };
  };

  // Load widget preferences on mount and whenever the authenticated user changes.
  // On first mount (user=null): loads from localStorage.
  // When user logs in (user.id available): re-fetches from server (cross-device source of truth).
  // When user logs out: resets to localStorage / defaults.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadWidgetPreferences(); }, [user?.id]);

  const contextValue: WidgetManagerContextType = {
    widgets,
    loading,
    showControls,
    setShowControls,
    toggleWidget,
    configureWidget,
    moveWidget,
    reorderWidgets,
    resetToDefaults,
    getEnabledWidgets,
    getWidgetsByCategory,
    getWidgetConfig,
    loadWidgetPreferences
  };

  return (
    <WidgetManagerContext.Provider value={contextValue}>
      {children}
    </WidgetManagerContext.Provider>
  );
}

export function useWidgetManager() {
  const context = useContext(WidgetManagerContext);
  if (context === undefined) {
    throw new Error('useWidgetManager must be used within a WidgetManagerProvider');
  }
  return context;
}